// Şub 27, 2026 — Mail Arşivi (PostNL harici arşiv kurallarının aldığı ekler).
// PostNL dağıtım listelerinin kendi sayfası var; burada onlar hariç tüm
// mail arşiv kurallarının indirdiği ekler listelenir.
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from '../../lib/i18n';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Archive, Download, Search, Mail, Building2, Calendar, Tag, FileText, Loader2,
  Trash2, Trash,
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';

const POSTNL_TYPE = 'postnl_distribution';

export default function MailArchiveList() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [rules, setRules] = useState([]);
  const [firms, setFirms] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  // Filters
  const [q, setQ] = useState('');
  const [selectedRuleId, setSelectedRuleId] = useState('all');
  const [selectedFirmId, setSelectedFirmId] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Şub 27, 2026 — Toplu seçim/silme state'i
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [skip, setSkip] = useState(0);
  const limit = 100;

  const postnlRuleIds = useMemo(
    () => new Set(rules.filter((r) => r.archive_type === POSTNL_TYPE).map((r) => r.id)),
    [rules]
  );

  // Şub 27, 2026 — Sadece PostNL DIŞI kurallar bu sayfada görünür.
  const nonPostnlRules = useMemo(
    () => rules.filter((r) => r.archive_type !== POSTNL_TYPE),
    [rules]
  );

  const firmNameById = useMemo(() => {
    const m = new Map();
    firms.forEach((f) => m.set(f.id, f.name));
    return m;
  }, [firms]);

  const ruleLabelById = useMemo(() => {
    const m = new Map();
    rules.forEach((r) => m.set(r.id, r.tag_label || r.label || r.id));
    return m;
  }, [rules]);

  const fetchRulesAndFirms = useCallback(async () => {
    try {
      const [rulesRes, firmsRes] = await Promise.all([
        api.get('/mail/archive-rules'),
        api.get('/admin/firms'),
      ]);
      const rulesData = rulesRes.data?.data ?? rulesRes.data ?? [];
      setRules(Array.isArray(rulesData) ? rulesData : []);
      const firmsPayload = firmsRes.data?.data ?? firmsRes.data ?? [];
      const firmsList = Array.isArray(firmsPayload)
        ? firmsPayload
        : Array.isArray(firmsPayload?.firms) ? firmsPayload.firms : [];
      setFirms(firmsList);
    } catch (e) {
      console.error('Failed to load rules/firms:', e);
    }
  }, []);

  const fetchArchive = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit };
      if (q.trim()) params.q = q.trim();
      if (selectedFirmId !== 'all') params.firm_id = selectedFirmId;
      if (selectedYear !== 'all') params.year = parseInt(selectedYear, 10);
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/mail/archive', { params });
      let data = res.data?.data || [];
      // Şub 27, 2026 — PostNL kayıtlarını 2 katmanlı filtrele:
      // 1) Doğrudan archive_type field'ı (yeni kayıtlarda + backfill'de)
      // 2) rule_id → rule.archive_type (rule hâlâ mevcutsa)
      data = data.filter((item) => {
        if (item.archive_type === POSTNL_TYPE) return false;
        if (postnlRuleIds.has(item.rule_id)) return false;
        return true;
      });
      // Rule filter (client-side)
      if (selectedRuleId !== 'all') {
        data = data.filter((item) => item.rule_id === selectedRuleId);
      }
      setItems(data);
      setTotal(res.data?.total || data.length);
    } catch (e) {
      console.error('Failed to load archive:', e);
      toast.error('Arşiv yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [q, selectedFirmId, selectedYear, selectedRuleId, dateFrom, dateTo, skip, postnlRuleIds]);

  // Şub 27, 2026 — Tekli silme
  const deleteOne = async (id, filename) => {
    if (!window.confirm(`"${filename}" arşivden silinsin mi? (S3 + DPD ingest log da temizlenir)`)) return;
    try {
      await api.delete(`/mail/archive/${id}`);
      toast.success('Silindi');
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
      fetchArchive();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silinemedi');
    }
  };

  // Toplu silme
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} kayıt silinsin mi? (S3 dosyaları ve DPD ingest log da temizlenir — geri alınamaz)`)) return;
    setDeleting(true);
    try {
      const r = await api.post('/mail/archive/bulk-delete', { ids: Array.from(selectedIds) });
      const d = r.data?.data || {};
      toast.success(`${d.deleted || 0} kayıt silindi (S3: ${d.s3_deleted || 0}, ingest log: ${d.cleaned_ingest || 0})`);
      setSelectedIds(new Set());
      fetchArchive();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toplu silme başarısız');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === items.length) return new Set();
      return new Set(items.map((i) => i.id));
    });
  };

  useEffect(() => {
    fetchRulesAndFirms();
  }, [fetchRulesAndFirms]);

  useEffect(() => {
    // Rules yüklendikten sonra tetikle
    if (rules.length > 0) fetchArchive();
    else if (rules.length === 0) {
      // Rules boşken yine de dene (sistemde hiç kural yoksa)
      fetchArchive();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, selectedFirmId, selectedYear, selectedRuleId, skip, rules.length]);

  const handleDownload = async (att) => {
    setDownloadingId(att.id);
    try {
      const res = await api.get(`/mail/attachments/${att.attachment_id}/download`, {
        responseType: 'blob',
      });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = att.filename || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error('Download failed:', e);
      toast.error('Dosya indirilemedi');
    } finally {
      setDownloadingId(null);
    }
  };

  const years = useMemo(() => {
    const s = new Set();
    items.forEach((i) => { if (i.tag_year) s.add(i.tag_year); });
    // Rules'dan da mevcut yılları çıkarabiliriz ama basit tutalım
    const arr = Array.from(s).sort((a, b) => b - a);
    if (arr.length === 0) {
      const cy = new Date().getFullYear();
      return [cy, cy - 1, cy - 2];
    }
    return arr;
  }, [items]);

  const formatDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes <= 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6" data-testid="mail-archive-list-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-blue-600" />
            {t('nav.mailArchive') || 'Mail Arşivi'}
          </CardTitle>
          <CardDescription>
            {t('mailArchive.description') ||
              'Mail Arşiv Kuralları tarafından otomatik olarak alınan ekler burada listelenir. PostNL dağıtım listeleri için ayrı sayfa kullanın.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filter bar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t('mailArchive.searchPlaceholder') || 'Dosya/konu/gönderen ara...'}
                value={q}
                onChange={(e) => { setQ(e.target.value); setSkip(0); }}
                className="pl-9"
                data-testid="mail-archive-search"
              />
            </div>
            <Select
              value={selectedRuleId}
              onValueChange={(v) => { setSelectedRuleId(v); setSkip(0); }}
            >
              <SelectTrigger data-testid="mail-archive-rule-filter">
                <SelectValue placeholder="Kural seç" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('mailArchive.allRules') || 'Tüm Kurallar'}</SelectItem>
                {nonPostnlRules.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.tag_label || r.label || r.name || r.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedFirmId}
              onValueChange={(v) => { setSelectedFirmId(v); setSkip(0); }}
            >
              <SelectTrigger data-testid="mail-archive-firm-filter">
                <SelectValue placeholder="Firma" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('mailArchive.allFirms') || 'Tüm Firmalar'}</SelectItem>
                {firms.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={String(selectedYear)}
              onValueChange={(v) => { setSelectedYear(v); setSkip(0); }}
            >
              <SelectTrigger data-testid="mail-archive-year-filter">
                <SelectValue placeholder="Yıl" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('mailArchive.allYears') || 'Tüm Yıllar'}</SelectItem>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Şub 27, 2026 — Tarih aralığı + Toplu silme */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="text-[10px] text-gray-500 uppercase tracking-wide">Tarih (mail geliş)</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setSkip(0); }}
                data-testid="mail-archive-date-from"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 uppercase tracking-wide">Bitiş</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setSkip(0); }}
                data-testid="mail-archive-date-to"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setDateFrom(''); setDateTo(''); setSkip(0); }}
                disabled={!dateFrom && !dateTo}
                className="w-full"
              >
                Tarih Sıfırla
              </Button>
            </div>
            <div className="flex items-end">
              {/* Toplu silme */}
              <Button
                variant="destructive"
                size="sm"
                onClick={bulkDelete}
                disabled={selectedIds.size === 0 || deleting}
                className="w-full"
                data-testid="mail-archive-bulk-delete"
              >
                {deleting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
                Seçileni Sil ({selectedIds.size})
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="text-sm text-gray-500 mb-2" data-testid="mail-archive-count">
            {loading ? '...' : `${items.length} kayıt${total > items.length ? ` / toplam ${total}` : ''}`}
          </div>

          {/* Table */}
          <div className="rounded-lg border overflow-x-auto">
            <Table className="table-fixed w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={items.length > 0 && selectedIds.size === items.length}
                      onCheckedChange={toggleSelectAll}
                      data-testid="mail-archive-select-all"
                    />
                  </TableHead>
                  <TableHead className="w-[280px]">
                    <FileText className="inline h-4 w-4 mr-1" />Dosya
                  </TableHead>
                  <TableHead className="w-[160px]">
                    <Tag className="inline h-4 w-4 mr-1" />Kural
                  </TableHead>
                  <TableHead className="w-[120px]">
                    <Building2 className="inline h-4 w-4 mr-1" />Firma
                  </TableHead>
                  <TableHead className="w-[260px]">
                    <Mail className="inline h-4 w-4 mr-1" />Gönderen / Konu
                  </TableHead>
                  <TableHead className="w-[130px]">
                    <Calendar className="inline h-4 w-4 mr-1" />Tarih
                  </TableHead>
                  <TableHead className="w-[110px] text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                      Arşivlenmiş dosya bulunamadı.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.map((it) => (
                  <TableRow key={it.id} data-testid={`archive-row-${it.id}`} className={selectedIds.has(it.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(it.id)}
                        onCheckedChange={() => toggleSelect(it.id)}
                        data-testid={`archive-select-${it.id}`}
                      />
                    </TableCell>
                    <TableCell className="w-[280px] max-w-[280px]">
                      <div className="flex flex-col min-w-0">
                        <span
                          className="font-medium truncate block"
                          title={it.filename || '(isimsiz)'}
                        >
                          {it.filename || '(isimsiz)'}
                        </span>
                        <span className="text-xs text-gray-500 truncate block">
                          {formatSize(it.size)}{it.content_type ? ` • ${it.content_type}` : ''}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="w-[160px] max-w-[160px]">
                      <Badge
                        variant="outline"
                        className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 max-w-full truncate inline-block align-middle"
                        title={it.tag_label || ruleLabelById.get(it.rule_id) || '-'}
                      >
                        {it.tag_label || ruleLabelById.get(it.rule_id) || '-'}
                      </Badge>
                    </TableCell>
                    <TableCell className="w-[120px] max-w-[120px]">
                      {it.tag_firm_id ? (
                        <Badge
                          variant="secondary"
                          className="max-w-full truncate inline-block align-middle"
                          title={firmNameById.get(it.tag_firm_id) || it.tag_firm_id}
                        >
                          {firmNameById.get(it.tag_firm_id) || it.tag_firm_id.slice(0, 8)}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="w-[260px] max-w-[260px]">
                      <div className="flex flex-col text-xs min-w-0">
                        <span
                          className="text-gray-700 dark:text-gray-300 truncate block"
                          title={it.from_addr || '-'}
                        >
                          {it.from_addr || '-'}
                        </span>
                        <span
                          className="text-gray-500 truncate block"
                          title={it.subject || '-'}
                        >
                          {it.subject || '-'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="w-[130px] max-w-[130px] whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">
                      {formatDate(it.archived_at || it.date)}
                    </TableCell>
                    <TableCell className="w-[80px] text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(it)}
                        disabled={downloadingId === it.id}
                        data-testid={`archive-download-${it.id}`}
                      >
                        {downloadingId === it.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteOne(it.id, it.filename)}
                        title="Bu kaydı sil"
                        data-testid={`archive-delete-${it.id}`}
                      >
                        <Trash className="h-4 w-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSkip(Math.max(0, skip - limit))}
                disabled={skip === 0 || loading}
              >
                Önceki
              </Button>
              <span className="text-sm text-gray-500">
                {skip + 1} - {Math.min(skip + limit, total)} / {total}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSkip(skip + limit)}
                disabled={skip + limit >= total || loading}
              >
                Sonraki
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
